class QuickAddModal {
  constructor() {
    this.modal = document.getElementById("quick-add-modal");
    this.modalContent = document.getElementById("quick-add-modal-content");
    this.closeButton = document.querySelector(".quick-add-modal__close");
    this.currentProductHandle = null;
    this.currentProductData = null;
    this.currentZoomIndex = 0; // Track current zoomed image index

    this.initEvents();
  }

  initEvents() {
    this.closeButton.addEventListener("click", () => this.close());
    this.modal.addEventListener("click", (e) => {
      if (e.target === this.modal) this.close();
    });
    document.addEventListener("keydown", (e) => {
      if (e.key === "Escape") this.close();
    });

    // Handle quick-add buttons - both direct add and modal
    document.addEventListener("click", (e) => {
      const quickAddBtn = e.target.closest(".quick-add-btn");
      if (!quickAddBtn) return;

      e.preventDefault();
      const productHandle = quickAddBtn.dataset.productHandle;
      const hasVariants = quickAddBtn.dataset.hasVariants === "true";
      const variantId = quickAddBtn.dataset.variantId;

      if (!productHandle) return;

      // Direct add to cart for single variant products
      if (!hasVariants && variantId) {
        this.addToCartDirect(variantId, quickAddBtn);
      } else {
        // Open modal for products with variants
        this.open(productHandle);
      }
    });
  }

  open(productHandle) {
    if (
      this.currentProductHandle === productHandle &&
      this.modal.style.display === "flex"
    ) {
      return;
    }

    this.currentProductHandle = productHandle;
    this.showLoading();
    this.modal.style.display = "flex";
    document.body.style.overflow = "hidden";

    this.fetchProduct(productHandle);
  }

  close() {
    this.modal.style.display = "none";
    document.body.style.overflow = "";
    this.currentProductHandle = null;
    this.currentProductData = null;
  }

  async addToCartDirect(variantId, button) {
    const originalText =
      button.querySelector(".quick-add-btn-label")?.textContent ||
      button.textContent;
    const labelElement = button.querySelector(".quick-add-btn-label");

    // Disable button and show loading state
    button.disabled = true;
    if (labelElement) {
      labelElement.textContent = "Adding...";
    } else {
      button.textContent = "Adding...";
    }

    try {
      const cartUrl = window.routes.cart_add_url + ".js";
      const response = await fetch(cartUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          items: [
            {
              id: variantId,
              quantity: 1,
            },
          ],
        }),
      });

      const data = await response.json();

      if (response.ok) {
        // Publish cart update event
        publish(PUB_SUB_EVENTS.cartUpdate, {
          action: "add",
          variant: data,
          productId: button.dataset.productId,
        });

        // Show success state
        if (labelElement) {
          labelElement.textContent = "Added!";
        } else {
          button.textContent = "Added!";
        }

        // Reset after delay
        setTimeout(() => {
          button.disabled = false;
          if (labelElement) {
            labelElement.textContent = originalText;
          } else {
            button.textContent = originalText;
          }
        }, 1500);
      } else {
        throw new Error(data.description || "Failed to add to cart");
      }
    } catch (error) {
      console.error("Error adding to cart:", error);

      // Publish error event
      publish(PUB_SUB_EVENTS.cartError, {
        error: error.message,
        context: "quick-add-direct",
        productId: button.dataset.productId,
      });

      if (labelElement) {
        labelElement.textContent = "Error - Try Again";
      } else {
        button.textContent = "Error - Try Again";
      }

      setTimeout(() => {
        button.disabled = false;
        if (labelElement) {
          labelElement.textContent = originalText;
        } else {
          button.textContent = originalText;
        }
      }, 2000);
    }
  }

  showLoading() {
    this.modalContent.innerHTML = `
      <div class="quick-add-loading">
        <p class="loading-text">Loading product</p>
        <div class="loading-dots">
          <span class="dot"></span>
          <span class="dot"></span>
          <span class="dot"></span>
        </div>
      </div>
    `;
  }

  showError() {
    this.modalContent.innerHTML = `
      <div class="quick-add-error">
        <p>Failed to load product. Please try again.</p>
        <button onclick="quickAddModal.close()">Close</button>
      </div>
    `;
  }

  async fetchProduct(productHandle) {
    try {
      const response = await fetch(
        `/products/${productHandle}?section_id=product-quick-add`
      );
      if (!response.ok) throw new Error("Failed to fetch product");

      const text = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(text, "text/html");
      const productHTML = doc.querySelector(".quick-add-product");

      if (!productHTML) throw new Error("Invalid product content");

      this.modalContent.innerHTML = "";
      this.modalContent.appendChild(productHTML);

      // Store product variants data
      const variantsData = productHTML.dataset.productVariants;
      if (variantsData) {
        this.currentProductData = JSON.parse(variantsData);
      }

      this.initProductScripts();
    } catch (error) {
      console.error("Quick add error:", error);
      this.showError();
    }
  }

  initProductScripts() {
    const productContainer =
      this.modalContent.querySelector(".quick-add-product");
    if (!productContainer) return;

    const productHandle = productContainer.dataset.productHandle;
    if (!productHandle) return;

    this.initGalleryQuickAdd(productContainer);
    this.initVariantSelectionQuickAdd(productContainer);
    this.initRequestCallTrigger(productContainer);
    this.initNoneColorButtonQuickAdd(productContainer);
    const form = productContainer.querySelector(".quick-add-form");
    if (form) {
      this.initQuantitySelector(productContainer);
      this.initFormSubmission(productContainer);
    }
  }

  normalizeForColorMatch(str) {
    if (typeof str !== "string" || !str.trim()) return "";
    return str
      .toLowerCase()
      .trim()
      .replace(/\s*\/\s*/g, "-")
      .replace(/\//g, "-")
      .replace(/\s+/g, "-")
      .replace(/-+/g, "-")
      .replace(/^-|-$/g, "");
  }

  getColorFilterConfigQuickAdd(container) {
    const idx = container.dataset.colorOptionIndex;
    const vals = container.dataset.colorValues;
    if (idx === undefined || idx === "" || vals === undefined || vals === "")
      return null;
    const colorIndex = parseInt(idx, 10);
    if (isNaN(colorIndex) || colorIndex < 0) return null;
    try {
      const values = JSON.parse(vals);
      if (!Array.isArray(values) || values.length < 2) return null;
      return { index: colorIndex, values };
    } catch (e) {
      return null;
    }
  }

  filterGalleryByColorQuickAdd(container, selectedColor) {
    const slider = container.querySelector("#quick-add-media-slider");
    const hiddenClassSlide = "gallery-slide--hidden-by-color";
    const hiddenClassThumb = "thumbnail-item--hidden-by-color";
    let allSlides = slider
      ? Array.from(slider.querySelectorAll(".gallery-slide"))
      : [];
    let allThumbs = Array.from(container.querySelectorAll(".thumbnail-item"));
    const config = this.getColorFilterConfigQuickAdd(container);
    const isNone =
      selectedColor && String(selectedColor).trim().toLowerCase() === "none";
    if (!config || !selectedColor || !selectedColor.trim() || isNone) {
      allSlides.forEach((s) => s.classList.remove(hiddenClassSlide));
      allThumbs.forEach((t) => t.classList.remove(hiddenClassThumb));
      if (container) container._quickAddSlideIndex = 0;
      this.updateQuickAddSliderPosition(container, 0);
      return;
    }
    const selectedColorNorm = this.normalizeForColorMatch(selectedColor);
    const normalizedList = config.values
      .map((v) => ({
        value: v,
        normalized: this.normalizeForColorMatch(String(v)),
      }))
      .filter((o) => o.normalized)
      .sort((a, b) => b.normalized.length - a.normalized.length);
    const slideVisible = (slide, selNorm, list) => {
      const alt = (slide.dataset.mediaAlt || "").trim();
      if (!alt) return true;
      const normAlt = this.normalizeForColorMatch(alt);
      if (!normAlt) return true;
      for (let i = 0; i < list.length; i++) {
        const n = list[i].normalized;
        if (n && normAlt.indexOf(n) !== -1) return n === selNorm;
      }
      return true;
    };
    allSlides.forEach((slide) => {
      const visible = slideVisible(slide, selectedColorNorm, normalizedList);
      slide.classList.toggle(hiddenClassSlide, !visible);
    });
    allThumbs.forEach((thumb) => {
      const mediaAlt = (thumb.dataset.mediaAlt || "").trim();
      if (!mediaAlt) {
        thumb.classList.remove(hiddenClassThumb);
        return;
      }
      const normAlt = this.normalizeForColorMatch(mediaAlt);
      if (!normAlt) {
        thumb.classList.remove(hiddenClassThumb);
        return;
      }
      let assigned = null;
      for (let j = 0; j < normalizedList.length; j++) {
        if (
          normalizedList[j].normalized &&
          normAlt.indexOf(normalizedList[j].normalized) !== -1
        ) {
          assigned = normalizedList[j].normalized;
          break;
        }
      }
      thumb.classList.toggle(
        hiddenClassThumb,
        assigned !== null && assigned !== selectedColorNorm
      );
    });
    if (container) container._quickAddSlideIndex = 0;
    this.updateQuickAddSliderPosition(container, 0);
  }

  updateQuickAddSliderPosition(container, index) {
    const slider = container.querySelector("#quick-add-media-slider");
    if (!slider) return;
    const allSlides = Array.from(slider.querySelectorAll(".gallery-slide"));
    const visibleSlides = allSlides.filter(
      (s) => !s.classList.contains("gallery-slide--hidden-by-color")
    );
    if (visibleSlides.length === 0) return;
    const idx = Math.max(0, Math.min(index, visibleSlides.length - 1));
    const translatePercent = idx * 100;
    slider.style.transition = "none";
    slider.style.transform = `translateX(-${translatePercent}%)`;
    const targetSlide = visibleSlides[idx];
    const dataIndex = targetSlide.getAttribute("data-media-index");
    container.querySelectorAll(".thumbnail-item").forEach((thumb) => {
      thumb.classList.toggle(
        "active",
        thumb.getAttribute("data-thumb-index") === dataIndex &&
          !thumb.classList.contains("thumbnail-item--hidden-by-color")
      );
    });
    const bar = container.querySelector(".thumbnail-bar-inner");
    if (bar) {
      const activeThumb = container.querySelector(".thumbnail-item.active");
      if (activeThumb)
        bar.scrollTo({
          left: Math.max(
            0,
            activeThumb.offsetLeft -
              bar.clientWidth / 2 +
              activeThumb.offsetWidth / 2
          ),
          behavior: "smooth",
        });
    }
  }

  initGalleryQuickAdd(container) {
    const slider = container.querySelector("#quick-add-media-slider");
    const prevBtn = container.querySelector(".quick-add-slider-prev");
    const nextBtn = container.querySelector(".quick-add-slider-next");
    if (!slider || !slider.querySelector(".gallery-slide")) return;
    container._quickAddSlideIndex = 0;
    const go = (delta) => {
      const allSlides = Array.from(slider.querySelectorAll(".gallery-slide"));
      const visible = allSlides.filter(
        (s) => !s.classList.contains("gallery-slide--hidden-by-color")
      );
      if (visible.length === 0) return;
      container._quickAddSlideIndex =
        (container._quickAddSlideIndex + delta + visible.length) %
        visible.length;
      this.updateQuickAddSliderPosition(
        container,
        container._quickAddSlideIndex
      );
    };
    const thumbPrevBtn = container.querySelector(".quick-add-thumb-prev");
    const thumbNextBtn = container.querySelector(".quick-add-thumb-next");
    if (prevBtn) prevBtn.addEventListener("click", () => go(-1));
    if (nextBtn) nextBtn.addEventListener("click", () => go(1));
    if (thumbPrevBtn) thumbPrevBtn.addEventListener("click", () => go(-1));
    if (thumbNextBtn) thumbNextBtn.addEventListener("click", () => go(1));
    container.querySelectorAll(".thumbnail-item").forEach((thumb) => {
      thumb.addEventListener("click", () => {
        const allSlides = Array.from(slider.querySelectorAll(".gallery-slide"));
        const visible = allSlides.filter(
          (s) => !s.classList.contains("gallery-slide--hidden-by-color")
        );
        const dataIndex = thumb.getAttribute("data-thumb-index");
        const idx = visible.findIndex(
          (s) => s.getAttribute("data-media-index") === dataIndex
        );
        if (idx >= 0) {
          container._quickAddSlideIndex = idx;
          this.updateQuickAddSliderPosition(container, idx);
        }
      });
    });
    const productData = container.dataset.productData
      ? JSON.parse(container.dataset.productData)
      : null;
    const variantInput = container.querySelector(".quick-add-variant-id");
    let selectedColor = null;
    const config = this.getColorFilterConfigQuickAdd(container);
    if (config && variantInput && productData && productData.variants) {
      const vid = parseInt(variantInput.value, 10);
      const v = productData.variants.find((x) => x.id === vid);
      if (
        v &&
        (v.options || [v.option1, v.option2, v.option3].filter(Boolean))[
          config.index
        ] !== undefined
      )
        selectedColor = (v.options ||
          [v.option1, v.option2, v.option3].filter(Boolean))[config.index];
    }
    container._quickAddSlideIndex = 0;
    this.filterGalleryByColorQuickAdd(container, selectedColor || null);
  }

  getSelectedOptionsQuickAdd(container) {
    const productData = container.dataset.productData
      ? JSON.parse(container.dataset.productData)
      : null;
    const optionCount =
      productData && productData.options ? productData.options.length : 3;
    const opts = [];
    for (let i = 0; i < optionCount; i++) opts[i] = null;
    container
      .querySelectorAll(".variant-option-input:checked")
      .forEach((input) => {
        const idx = parseInt(input.getAttribute("data-option-index"), 10);
        if (!isNaN(idx) && idx >= 0 && idx < optionCount)
          opts[idx] = input.value;
      });
    return opts;
  }

  findVariantByOptionsQuickAdd(container, options) {
    const productData = container.dataset.productData
      ? JSON.parse(container.dataset.productData)
      : null;
    if (!productData || !productData.variants) return null;
    return productData.variants.find((v) => {
      const o1 = v.option1 || (v.options && v.options[0]);
      const o2 = v.option2 || (v.options && v.options[1]);
      const o3 = v.option3 || (v.options && v.options[2]);
      return (
        (options[0] == null || options[0] === o1) &&
        (options[1] == null || options[1] === o2) &&
        (options[2] == null || options[2] === o3)
      );
    });
  }

  updateOptionButtonImagesQuickAdd(container) {
    const productData = container.dataset.productData
      ? JSON.parse(container.dataset.productData)
      : null;
    const optionCount =
      productData && productData.options
        ? productData.options.length
        : 3;
    const grids = container.querySelectorAll(".option-buttons-grid");
    grids.forEach((grid) => {
      const firstInput = grid.querySelector(".variant-option-input");
      const optionIndex =
        firstInput != null
          ? parseInt(firstInput.getAttribute("data-option-index"), 10)
          : -1;
      if (isNaN(optionIndex) || optionIndex < 0) return;
      const currentOpts = this.getSelectedOptionsQuickAdd(container);
      grid.querySelectorAll(".option-button-wrap").forEach((wrap) => {
        const input = wrap.querySelector(".variant-option-input");
        const value = input ? input.value : null;
        if (value == null) return;
        const opts = [];
        for (let i = 0; i < optionCount; i++)
          opts[i] = i === optionIndex ? value : currentOpts[i];
        const v = this.findVariantByOptionsQuickAdd(container, opts);
        const img = wrap.querySelector(".option-button-img");
        if (!img) return;
        let imgSrc = null;
        let imgAlt = value;
        if (v) {
          if (v.featured_image && v.featured_image.src) {
            imgSrc =
              v.featured_image.src.split("?")[0] + "?width=288";
            if (v.featured_image.alt && String(v.featured_image.alt).trim())
              imgAlt = v.featured_image.alt;
          } else if (
            v.featured_media &&
            v.featured_media.preview_image &&
            v.featured_media.preview_image.src
          ) {
            imgSrc =
              v.featured_media.preview_image.src.split("?")[0] + "?width=288";
            if (v.featured_media.alt && String(v.featured_media.alt).trim())
              imgAlt = v.featured_media.alt;
          }
        }
        if (imgSrc) {
          img.src = imgSrc;
          img.alt = imgAlt;
          img.style.display = "";
        } else {
          img.style.display = "none";
        }
      });
    });
  }

  updatePricesQuickAdd(container, variant) {
    if (!variant) return;
    const currentPrice = container.querySelector(
      "[data-product-current-price], .current-price"
    );
    const comparePrice = container.querySelector(
      "[data-product-compare-price], .compare-price"
    );
    const savingsEl = container.querySelector(
      "[data-product-savings], .savings"
    );
    if (!currentPrice) return;
    const fmt =
      window.money_without_trailing_zeros_format || window.money_format;
    const formatMoney = (cents) => {
      if (window.Shopify && typeof window.Shopify.formatMoney === "function") {
        let s = window.Shopify.formatMoney(cents, fmt);
        if (typeof s === "string")
          s = s
            .replace(/\.0+$/, "")
            .replace(/(\.\d*[1-9])0+$/, "$1")
            .replace(/\.$/, "");
        return s;
      }
      return "$" + (Number(cents) / 100).toFixed(2).replace(/\.?0+$/, "");
    };
    currentPrice.textContent = formatMoney(variant.price);
    if (comparePrice)
      comparePrice.textContent = formatMoney(variant.compare_at_price);
    if (
      variant.compare_at_price != null &&
      variant.compare_at_price > variant.price
    ) {
      if (comparePrice) comparePrice.style.display = "";
      if (savingsEl) {
        savingsEl.textContent =
          "Save " + formatMoney(variant.compare_at_price - variant.price);
        savingsEl.style.display = "";
      }
    } else {
      if (comparePrice) comparePrice.style.display = "none";
      if (savingsEl) savingsEl.style.display = "none";
    }
  }

  updateNoneColorButtonStateQuickAdd(container) {
    const wrapper = container.querySelector("[data-cant-decide-wrapper]");
    if (!wrapper) return;
    const btn = wrapper.querySelector("[data-none-color-select]");
    if (!btn) return;
    const colorIndex = parseInt(
      wrapper.getAttribute("data-color-option-index"),
      10
    );
    const noneValue = wrapper.getAttribute("data-none-value") || "None";
    const opts = this.getSelectedOptionsQuickAdd(container);
    const isNone = opts[colorIndex] === noneValue;
    btn.classList.toggle("is-checked", isNone);
    btn.setAttribute("aria-pressed", isNone ? "true" : "false");
  }

  initVariantSelectionQuickAdd(container) {
    const variantInput = container.querySelector(".quick-add-variant-id");
    if (!variantInput) return;
    const productData = container.dataset.productData
      ? JSON.parse(container.dataset.productData)
      : null;
    if (!productData || !productData.variants) return;
    const applyColorFilter = (variant) => {
      const config = this.getColorFilterConfigQuickAdd(container);
      if (!config) {
        this.filterGalleryByColorQuickAdd(container, null);
        return;
      }
      const opts =
        variant &&
        (variant.options ||
          [variant.option1, variant.option2, variant.option3].filter(Boolean));
      let selectedColor =
        opts && opts[config.index] !== undefined ? opts[config.index] : null;
      if (!selectedColor) {
        const checked = container.querySelector(
          `.variant-option-input[data-option-index="${config.index}"]:checked`
        );
        if (checked) selectedColor = checked.value;
      }
      const isNone =
        selectedColor && String(selectedColor).trim().toLowerCase() === "none";
      this.filterGalleryByColorQuickAdd(
        container,
        isNone ? null : selectedColor || null
      );
    };
    const handleChange = () => {
      const opts = this.getSelectedOptionsQuickAdd(container);
      const variant = this.findVariantByOptionsQuickAdd(container, opts);
      if (variant) {
        variantInput.value = variant.id;
        this.updatePricesQuickAdd(container, variant);
        applyColorFilter(variant);
      }
      this.updateOptionButtonImagesQuickAdd(container);
      this.updateNoneColorButtonStateQuickAdd(container);
    };
    container
      .querySelectorAll(".product-swatch, .option-button-label")
      .forEach((label) => {
        label.addEventListener("click", (e) => {
          e.preventDefault();
          const forId = label.getAttribute("for");
          if (!forId) return;
          const input = document.getElementById(forId);
          if (input && input.classList.contains("variant-option-input")) {
            input.checked = true;
            container
              .querySelectorAll(".product-swatch")
              .forEach((l) => l.classList.remove("product-swatch--selected"));
            container
              .querySelectorAll(".option-button-label")
              .forEach((l) =>
                l.classList.remove("option-button-label--active")
              );
            if (label.classList.contains("product-swatch"))
              label.classList.add("product-swatch--selected");
            if (label.classList.contains("option-button-label"))
              label.classList.add("option-button-label--active");
            handleChange();
          }
        });
      });
    container.addEventListener("change", (e) => {
      if (e.target && e.target.classList.contains("variant-option-input"))
        handleChange();
    });
    const noneBtn = container.querySelector("[data-none-color-select]");
    if (noneBtn) {
      noneBtn.addEventListener("click", (e) => {
        e.preventDefault();
        const input = container.querySelector("input[data-none-color-input]");
        if (input && input.classList.contains("variant-option-input")) {
          input.checked = true;
          handleChange();
        }
      });
    }
    handleChange();
    this.updateOptionButtonImagesQuickAdd(container);
  }

  initNoneColorButtonQuickAdd(container) {
    this.updateNoneColorButtonStateQuickAdd(container);
  }

  initRequestCallTrigger(container) {
    const trigger = container.querySelector(".quick-add-request-call-trigger");
    if (!trigger) return;
    trigger.addEventListener("click", () => {
      const variantInput = container.querySelector(".quick-add-variant-id");
      const mainVariantId = variantInput ? variantInput.value : null;
      const upgradeInputs = container.querySelectorAll(
        '[data-upsell-variant-input][data-has-value="true"]'
      );
      const items = [];
      if (mainVariantId) items.push({ id: mainVariantId, quantity: 1 });
      upgradeInputs.forEach((inp) => {
        const val = inp.value ? inp.value.trim() : "";
        if (val) items.push({ id: val, quantity: 1 });
      });
      const openPopup = () => {
        if (typeof window.openRequestCallPopup === "function")
          window.openRequestCallPopup();
      };
      if (items.length > 0) {
        trigger.disabled = true;
        const originalText = trigger.querySelector(".request-call-btn-text");
        const btnText = originalText
          ? originalText.textContent
          : trigger.textContent;
        if (originalText) originalText.textContent = "Preparing";
        const checkedBtns = [];
        upgradeInputs.forEach((inp) => {
          const wrap = inp.closest(".product-upsell__item-wrap");
          const btn = wrap
            ? wrap.querySelector("[data-upgrade-drawer-trigger]")
            : null;
          if (btn) {
            btn.classList.add("product-upsell__item--loading");
            checkedBtns.push(btn);
          }
        });
        const cartUrl =
          (window.routes && window.routes.cart_add_url
            ? window.routes.cart_add_url
            : "/cart/add") + ".js";
        fetch(cartUrl, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ items }),
        })
          .then((r) => r.json())
          .then(() => openPopup())
          .catch(() => openPopup())
          .finally(() => {
            trigger.disabled = false;
            if (originalText) originalText.textContent = btnText;
            checkedBtns.forEach((b) =>
              b.classList.remove("product-upsell__item--loading")
            );
          });
      } else openPopup();
    });
  }

  initImageSlider(container) {
    const slider = container.querySelector(".quick-add-slides");
    const slides = container.querySelectorAll(".quick-add-slide");
    const prevBtn = container.querySelector(".quick-add-prev");
    const nextBtn = container.querySelector(".quick-add-next");
    const counter = container.querySelector(
      ".quick-add-counter .current-slide"
    );

    if (!slider || slides.length === 0) return;

    let currentSlide = 0;
    const slideCount = slides.length;

    const updateCounter = () => {
      if (counter) counter.textContent = currentSlide + 1;
    };

    const goToSlide = (index) => {
      currentSlide = (index + slideCount) % slideCount;
      slider.style.transform = `translateX(-${currentSlide * 100}%)`;
      updateCounter();
    };

    const nextSlide = () => goToSlide(currentSlide + 1);
    const prevSlide = () => goToSlide(currentSlide - 1);

    if (nextBtn) nextBtn.addEventListener("click", nextSlide);
    if (prevBtn) prevBtn.addEventListener("click", prevSlide);

    // Touch events for swipe
    let touchStartX = 0;
    let touchEndX = 0;

    slider.addEventListener(
      "touchstart",
      (e) => {
        touchStartX = e.changedTouches[0].screenX;
      },
      { passive: true }
    );

    slider.addEventListener(
      "touchend",
      (e) => {
        touchEndX = e.changedTouches[0].screenX;
        if (touchEndX < touchStartX - 50) nextSlide();
        if (touchEndX > touchStartX + 50) prevSlide();
      },
      { passive: true }
    );

    // Keyboard navigation
    document.addEventListener("keydown", (e) => {
      if (e.key === "ArrowRight") nextSlide();
      if (e.key === "ArrowLeft") prevSlide();
    });

    updateCounter();
  }

  // Add these new methods for zoom functionality
  initImageZoom(container) {
    const zoomPopup = container.querySelector(".quick-image-zoom-popup");
    const zoomImages = container.querySelectorAll(".quick-zoom-image");
    const zoomImageContainer = container.querySelector(
      ".quick-zoom-image-container"
    );
    const zoomCloseBtn = container.querySelector(".quick-zoom-close-btn");
    const zoomPrevBtn = container.querySelector(".quick-zoom-nav.prev");
    const zoomNextBtn = container.querySelector(".quick-zoom-nav.next");
    const zoomCurrentSlide = container.querySelector(
      ".quick-zoom-counter .quick-current-slide"
    );
    const zoomTotalSlides = container.querySelector(
      ".quick-zoom-counter .quick-total-slides"
    );
    const mainZoomBtn = container.querySelector(".quick-main_zoom");

    if (!zoomPopup) return;

    // Initialize zoom popup
    const initZoomPopup = () => {
      zoomTotalSlides.textContent = zoomImages.length;

      // Open zoom popup
      mainZoomBtn.addEventListener("click", () => {
        const currentSlide = container.querySelector(
          ".quick-add-slide[data-index]"
        );
        this.currentZoomIndex = parseInt(currentSlide.dataset.index || "0");
        updateZoomPopup();
        zoomPopup.classList.add("active");
        document.body.style.overflow = "hidden";
      });

      // Close zoom popup
      zoomCloseBtn.addEventListener("click", () => {
        zoomPopup.classList.remove("active");
        document.body.style.overflow = "hidden"; // Keep hidden because modal is still open
      });

      // Close when clicking outside image
      zoomPopup.addEventListener("click", (e) => {
        if (e.target === zoomPopup) {
          zoomPopup.classList.remove("active");
          document.body.style.overflow = "hidden";
        }
      });

      // Keyboard navigation
      document.addEventListener("keydown", (e) => {
        if (zoomPopup.classList.contains("active")) {
          if (e.key === "Escape") {
            zoomPopup.classList.remove("active");
            document.body.style.overflow = "hidden";
          }
          if (e.key === "ArrowRight") nextZoomImage();
          if (e.key === "ArrowLeft") prevZoomImage();
        }
      });

      // Navigation buttons
      zoomPrevBtn?.addEventListener("click", (e) => {
        e.stopPropagation();
        prevZoomImage();
      });

      zoomNextBtn?.addEventListener("click", (e) => {
        e.stopPropagation();
        nextZoomImage();
      });

      // Swipe gestures for mobile
      let touchStartX = 0;
      let touchEndX = 0;

      zoomImageContainer.addEventListener(
        "touchstart",
        (e) => {
          touchStartX = e.changedTouches[0].screenX;
        },
        { passive: true }
      );

      zoomImageContainer.addEventListener(
        "touchend",
        (e) => {
          touchEndX = e.changedTouches[0].screenX;
          handleZoomSwipe();
        },
        { passive: true }
      );

      const handleZoomSwipe = () => {
        if (touchEndX < touchStartX - 50) nextZoomImage();
        if (touchEndX > touchStartX + 50) prevZoomImage();
      };

      const nextZoomImage = () => {
        this.currentZoomIndex = (this.currentZoomIndex + 1) % zoomImages.length;
        updateZoomPopup();
      };

      const prevZoomImage = () => {
        this.currentZoomIndex =
          (this.currentZoomIndex - 1 + zoomImages.length) % zoomImages.length;
        updateZoomPopup();
      };

      const updateZoomPopup = () => {
        zoomImages.forEach((img, index) => {
          img.classList.toggle("active", index === this.currentZoomIndex);
        });
        if (zoomCurrentSlide) {
          zoomCurrentSlide.textContent = this.currentZoomIndex + 1;
        }
      };
    };

    initZoomPopup.call(this); // Call with this context
  }

  initVariantSelection(container, productHandle) {
    const variantRadios = container.querySelectorAll(
      '.variant-option input[type="radio"]'
    );
    const addToCartBtn = container.querySelector(".add-to-cart-btn");
    const variantInput = container.querySelector('input[name="id"]');
    const currentPriceEl = container.querySelector(".current-price");
    const comparePriceEl = container.querySelector(".compare-price");
    const savingsEl = container.querySelector(".savings .save-discount");
    const stockDataElement = container.querySelector(
      "#quick-add-variant-stock-data"
    );
    const stockData = stockDataElement
      ? JSON.parse(stockDataElement.textContent)
      : null;
    const variantStockEls = container.querySelectorAll(
      ".variant-option .option-value"
    );

    // Get variant count for button text formatting
    const shopifyVariantCount =
      this.currentProductData && Array.isArray(this.currentProductData)
        ? this.currentProductData.length
        : 0;

    if (variantRadios.length === 0) return;

    // Initialize stock indicators
    if (stockData) {
      this.updateStockIndicators(container, stockData, variantStockEls, []);
    }

    // Set initial button text based on current variant
    if (
      addToCartBtn &&
      this.currentProductData &&
      this.currentProductData.length > 0
    ) {
      const currentVariantId = variantInput
        ? parseInt(variantInput.value)
        : null;
      if (currentVariantId) {
        const currentVariant = this.currentProductData.find(
          (v) => v.id === currentVariantId
        );
        if (currentVariant) {
          if (currentVariant.available) {
            let variantText = currentVariant.options
              ? currentVariant.options.join("/")
              : "";
            const addToCartText =
              window.translations?.cart?.add_to_cart || "Add to Cart";

            if (shopifyVariantCount > 1 && variantText) {
              addToCartBtn.textContent = `${addToCartText} - ${variantText}`;
            } else {
              addToCartBtn.textContent = addToCartText;
            }
            addToCartBtn.disabled = false;
          } else {
            addToCartBtn.disabled = true;
            addToCartBtn.textContent =
              window.translations?.cart?.sold_out || "Sold Out";
          }
        }
      }
    }

    variantRadios.forEach((radio) => {
      radio.addEventListener("change", () => {
        const selectedOptions = this.getSelectedOptions(container);
        const selectedVariant = this.findVariantByOptions(selectedOptions);

        if (selectedVariant) {
          if (variantInput) variantInput.value = selectedVariant.id;

          // Update price display (matching main-product format)
          if (currentPriceEl) {
            currentPriceEl.textContent = Shopify.formatMoney(
              selectedVariant.price,
              window.money_without_trailing_zeros_format || window.money_format
            );
          }

          if (comparePriceEl && savingsEl) {
            if (selectedVariant.compare_at_price > selectedVariant.price) {
              comparePriceEl.textContent = Shopify.formatMoney(
                selectedVariant.compare_at_price,
                window.money_without_trailing_zeros_format ||
                  window.money_format
              );
              comparePriceEl.style.display = "inline";
              const saveValue =
                100 -
                (selectedVariant.price / selectedVariant.compare_at_price) *
                  100;
              savingsEl.textContent = `${Math.round(saveValue)}% ${
                window.translations?.product?.saved || "Saved"
              }`;
              savingsEl.closest(".savings").style.display = "inline";
            } else {
              comparePriceEl.style.display = "none";
              savingsEl.closest(".savings").style.display = "none";
            }
          }

          // Update button (matching main-product format)
          if (addToCartBtn) {
            if (selectedVariant.available) {
              let variantText = selectedVariant.options
                ? selectedVariant.options.join("/")
                : "";
              const addToCartText =
                window.translations?.cart?.add_to_cart || "Add to Cart";

              if (shopifyVariantCount > 1 && variantText) {
                addToCartBtn.textContent = `${addToCartText} - ${variantText}`;
              } else {
                addToCartBtn.textContent = addToCartText;
              }
              addToCartBtn.disabled = false;
            } else {
              addToCartBtn.disabled = true;
              addToCartBtn.textContent =
                window.translations?.cart?.sold_out || "Sold Out";
            }
          }

          // Update stock indicators
          if (stockData) {
            this.updateStockIndicators(
              container,
              stockData,
              variantStockEls,
              selectedOptions
            );
          }

          // Update image if variant has featured image
          if (selectedVariant.featured_image) {
            const newImageSrc =
              selectedVariant.featured_image.src.split("?")[0] + "?width=800";
            const mainImage = container.querySelector(".quick-main-image");

            if (mainImage) {
              mainImage.src = newImageSrc;
              mainImage.alt = selectedVariant.featured_image.alt || "";
              mainImage.dataset.variantImage = selectedVariant.id;
            }
          }
        }
      });
    });
  }

  getSelectedOptions(container) {
    const selectedOptions = [];
    container.querySelectorAll(".variant-option").forEach((group) => {
      const checked = group.querySelector('input[type="radio"]:checked');
      const labels = group.querySelectorAll(".option-value");
      labels.forEach((label) => label.classList.remove("selected"));
      if (checked) {
        selectedOptions.push(checked.value);
        const checkedLabel = group.querySelector(`label[for="${checked.id}"]`);
        if (checkedLabel) checkedLabel.classList.add("selected");
      } else {
        selectedOptions.push(null);
      }
    });
    return selectedOptions;
  }

  findVariantByOptions(options) {
    if (!this.currentProductData) return null;
    return this.currentProductData.find((variant) => {
      return variant.options.every(
        (opt, idx) => !options[idx] || options[idx] === opt
      );
    });
  }

  updateStockIndicators(
    container,
    stockData,
    variantStockEls,
    selectedOptions = []
  ) {
    if (!stockData) return;
    const optionsCount = stockData.options.length;

    variantStockEls.forEach((el) => {
      const labelValue = el.getAttribute("title")?.trim();
      const fieldset = el.closest(".variant-option");
      const optionIndex = Array.from(fieldset.parentNode.children).indexOf(
        fieldset
      );

      // Remove old stock indicator
      el.querySelector(".variant-stock")?.remove();

      let stockLeft = 0;

      if (optionsCount === 1) {
        // Single option → sum directly
        stockLeft = stockData.variants
          .filter((v) => v.options[0] === labelValue)
          .reduce((acc, v) => acc + v.stock, 0);
      } else if (optionsCount >= 2) {
        if (optionIndex === 0) {
          // First-level (e.g., Size)
          stockLeft = stockData.variants
            .filter((v) => v.options[0] === labelValue)
            .reduce((acc, v) => acc + v.stock, 0);
        } else if (optionIndex === 1 && selectedOptions[0]) {
          // Second-level (e.g., Age) → based on selected Size
          stockLeft = stockData.variants
            .filter(
              (v) =>
                v.options[0] === selectedOptions[0] &&
                v.options[1] === labelValue
            )
            .reduce((acc, v) => acc + v.stock, 0);
        }
      }

      if (stockLeft > 0 && stockLeft < 10) {
        const urgent = stockLeft < 5;
        const isSwatch =
          el.hasAttribute("style") &&
          el.getAttribute("style").includes("--swatch");
        const div = document.createElement("div");
        div.className = `variant-stock ${
          isSwatch ? "variant-stock--swatch" : ""
        }`;
        div.dataset.stock = stockLeft;
        div.dataset.urgent = urgent ? "true" : "false";
        div.innerHTML = `
          <div class="dot ${urgent ? "urgent" : "warning"}"></div>
          ${stockLeft} left
        `;
        el.appendChild(div);
      }
    });
  }

  initQuantitySelector(container) {
    const minusBtn = container.querySelector(".quantity-btn.minus");
    const plusBtn = container.querySelector(".quantity-btn.plus");
    const quantityInput = container.querySelector(".quantity-input");
    const stockError = container.querySelector(".stock-error");
    const productContainer = container.closest(".quick-add-product");

    if (!minusBtn || !plusBtn || !quantityInput) return;

    const getMaxQuantity = () => {
      const variantInput = container.querySelector('input[name="id"]');
      if (!variantInput) return 9999;

      const variantId = parseInt(variantInput.value);
      const variant = this.currentProductData?.find((v) => v.id === variantId);

      if (!variant) return 9999;

      const inventoryPolicy = variant.inventory_policy;
      const inventoryManagement = variant.inventory_management;

      if (inventoryManagement === "shopify" && inventoryPolicy === "deny") {
        return variant.inventory_quantity || 9999;
      }
      return 9999; // No limit if not tracking inventory
    };

    const getMinQuantity = () => {
      const minAttr = quantityInput.getAttribute("data-min");
      return minAttr ? parseInt(minAttr) : 1;
    };

    minusBtn.addEventListener("click", () => {
      const currentValue = parseInt(quantityInput.value);
      const minValue = getMinQuantity();
      if (currentValue > minValue) {
        quantityInput.value = currentValue - 1;
        if (stockError) stockError.textContent = ""; // Clear error when decreasing
      }
    });

    plusBtn.addEventListener("click", () => {
      const currentValue = parseInt(quantityInput.value);
      const maxQuantity = getMaxQuantity();

      if (currentValue >= maxQuantity) {
        if (stockError)
          stockError.textContent = `Only ${maxQuantity} available in stock`;
        return;
      }

      quantityInput.value = currentValue + 1;
      if (stockError) stockError.textContent = ""; // Clear error when increasing within limits
    });

    quantityInput.addEventListener("change", () => {
      const maxQuantity = getMaxQuantity();
      const minValue = getMinQuantity();
      let newValue = parseInt(quantityInput.value) || minValue;

      if (newValue < minValue) newValue = minValue;
      if (newValue > maxQuantity) {
        newValue = maxQuantity;
        if (stockError)
          stockError.textContent = `Only ${maxQuantity} available in stock`;
      } else {
        if (stockError) stockError.textContent = "";
      }

      quantityInput.value = newValue;
    });
  }

  initFormSubmission(container) {
    const form = container.querySelector(".quick-add-form");
    if (!form) return;

    form.addEventListener("submit", async (e) => {
      e.preventDefault();

      const submitBtn = form.querySelector(".add-to-cart-btn");
      const originalText = submitBtn.textContent;
      const quantityInput = form.querySelector(".quantity-input");
      const stockError = form.querySelector(".stock-error");
      const variantInput = form.querySelector('input[name="id"]');

      if (!variantInput) return;

      const variantId = parseInt(variantInput.value);
      const variant = this.currentProductData?.find((v) => v.id === variantId);

      const quantity = quantityInput ? parseInt(quantityInput.value) : 1;
      const maxQuantity = variant?.inventory_quantity || 9999;
      const inventoryPolicy = variant?.inventory_policy;
      const inventoryManagement = variant?.inventory_management;

      // Validate stock
      if (
        inventoryManagement === "shopify" &&
        inventoryPolicy === "deny" &&
        quantity > maxQuantity
      ) {
        if (stockError)
          stockError.textContent = `Only ${maxQuantity} available in stock`;
        if (quantityInput) quantityInput.value = maxQuantity;
        return;
      }

      submitBtn.disabled = true;
      submitBtn.textContent = window.translations?.cart?.adding || "Adding...";

      try {
        const cartUrl = window.routes.cart_add_url + ".js";

        const response = await fetch(cartUrl, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            items: [
              {
                id: variantId,
                quantity: Math.min(quantity, maxQuantity),
              },
            ],
          }),
        });

        const data = await response.json();

        if (response.ok) {
          submitBtn.textContent = window.translations?.cart?.added || "Added!";

          // Publish cart update event
          publish(PUB_SUB_EVENTS.cartUpdate, {
            action: "add",
            variant: data,
            product: this.currentProductData,
          });

          // Close modal after delay
          setTimeout(() => {
            this.close();
            submitBtn.textContent = originalText;
            submitBtn.disabled = false;
          }, 1000);
        } else {
          throw new Error(data.description || "Failed to add to cart");
        }
      } catch (error) {
        console.error("Error adding to cart:", error);

        // Publish error event
        publish(PUB_SUB_EVENTS.cartError, {
          error: error.message,
          context: "quick-add-modal",
          productHandle: this.currentProductHandle,
        });

        submitBtn.textContent = "Error - Try Again";
        setTimeout(() => {
          submitBtn.textContent = originalText;
          submitBtn.disabled = false;
        }, 2000);
      }
    });
  }
}

// Initialize everything when DOM is loaded
document.addEventListener("DOMContentLoaded", () => {
  // Initialize global instances
  window.quickAddModal = new QuickAddModal();

  // Event delegation for color swatches
  document.addEventListener("click", function (e) {
    // Color swatches
    const colorOption = e.target.closest(
      ".card-product__color-option:not(.disabled)"
    );
    if (colorOption) {
      e.preventDefault();
      if (colorOption.getAttribute("aria-current") === "true") return;

      const productCard = colorOption.closest(".card-product");
      const desktopImage = productCard.querySelector(
        ".card-product__image-link.hidden-mobile .card-product__image"
      );
      const mobileImage = productCard.querySelector(
        ".card-product__image-container.visible-mobile .card-product__image"
      );

      // Update active state
      colorOption
        .closest(".card-product__colors")
        .querySelectorAll(".card-product__color-option")
        .forEach((btn) => btn.setAttribute("aria-current", "false"));
      colorOption.setAttribute("aria-current", "true");

      // Update images
      const newImageSrc = colorOption.dataset.imageSrc;
      const newMobileImageSrc = colorOption.dataset.imageSrcMobile;
      const newAltText = colorOption.dataset.imageAlt;

      if (desktopImage && newImageSrc) desktopImage.src = newImageSrc;
      if (mobileImage && newMobileImageSrc) mobileImage.src = newMobileImageSrc;
      if (desktopImage) desktopImage.alt = newAltText;
      if (mobileImage) mobileImage.alt = newAltText;
    }
  });
});
